import React, { useEffect, useRef, useState } from "react";
import { Modal, Spin } from "antd";
import { select } from "d3-selection";
import { scaleLinear, scaleTime } from "d3-scale";
import { line } from "d3-shape";
import { axisBottom, axisLeft } from "d3-axis";
import { extent, max, min } from "d3-array";
import { timeParse, timeFormat } from "d3-time-format";
import "./CompareTimeseriesModal.css";

const CENTER_COLORS = {
  DWD:   "#e3163b",
  ECMWF: "#2563eb",
  JMA:   "#16a34a",
  NCEP:  "#f97316",
};

const METRIC_LABELS = {
  availability: "Availability %",
  timeliness:   "Timeliness (min)",
  quality:      "O-B Departure",
};

const fmtY = (metric, v) => {
  if (v == null) return "—";
  if (metric === "availability") return `${v.toFixed(1)}%`;
  if (metric === "timeliness") return `${v.toFixed(1)} min`;
  return v.toFixed(4);
};

const CompareTimeseriesModal = ({
  visible,
  onClose,
  stationId,
  stationName,
  metric,
  filters,
}) => {
  const svgRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState(null);

  const buildQuery = (f) => {
    if (!f) return "";
    const parts = [];
    parts.push(`period_type=${f.periodType || "daily"}`);
    parts.push(`metric=${f.metric || metric || "availability"}`);
    parts.push(`var_id=${f.varId || "110"}`);
    if (f.date1) parts.push(`date1=${f.date1}`);
    if (f.date2) parts.push(`date2=${f.date2}`);
    const centers = Array.isArray(f.centers) ? f.centers.join(",") : (f.centers || "all");
    parts.push(`centers=${centers}`);
    return parts.join("&");
  };

  useEffect(() => {
    if (!visible || !stationId) return;
    setLoading(true);
    setRawData(null);

    const qs = buildQuery(filters);
    const url = `/wdqmsapi/v1/compare/station/timeseries/?${qs}&station_id=${stationId}`;

    fetch(url)
      .then((res) => {
        if (!res.ok) {
          console.error("[CompareTimeseriesModal] API error", res.status, res.statusText);
          return res.text().then((t) => { console.error("[CompareTimeseriesModal] response:", t); return null; });
        }
        return res.json();
      })
      .then((data) => {
        if (data) {
          setLoading(false);
          setRawData(data);
        } else {
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("[CompareTimeseriesModal] fetch error:", err);
        setLoading(false);
      });
  }, [visible, stationId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!rawData || !svgRef.current || rawData.centers == null) return;

    const svg = select(svgRef.current);
    svg.selectAll("*").remove();

    const container = svgRef.current.parentElement;
    const containerWidth = container.clientWidth || 720;
    const W = Math.min(containerWidth, 800);
    const H = 400;
    const margin = { top: 20, right: 80, bottom: 40, left: 60 };
    const width = W - margin.left - margin.right;
    const height = H - margin.top - margin.bottom;

    svg.attr("width", W).attr("height", H);

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const allDates = [];
    const allValues = [];
    const centerNames = Object.keys(rawData.centers);

    centerNames.forEach((center) => {
      rawData.centers[center].forEach((d) => {
        allDates.push(new Date(d.date));
        allValues.push(d.value);
      });
    });

    if (allDates.length === 0) return;

    const xScale = scaleTime()
      .domain(extent(allDates))
      .range([0, width]);

    const yMin = min(allValues);
    const yMax = max(allValues);
    const yPad = (yMax - yMin) * 0.1 || 1;
    const yScale = scaleLinear()
      .domain([yMin - yPad, yMax + yPad])
      .range([height, 0]);

    // X axis
    g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(
        axisBottom(xScale)
          .ticks(Math.min(allDates.length, 8))
          .tickFormat(timeFormat("%m-%d"))
      )
      .selectAll("text")
      .style("font-size", "10px");

    // Y axis
    g.append("g")
      .call(axisLeft(yScale).ticks(6))
      .selectAll("text")
      .style("font-size", "10px");

    // Y axis label
    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -height / 2)
      .attr("y", -45)
      .attr("text-anchor", "middle")
      .style("font-size", "11px")
      .style("fill", "#6b7280")
      .text(METRIC_LABELS[rawData.metric] || rawData.metric);

    // Grid lines
    g.append("g")
      .attr("class", "grid")
      .selectAll("line")
      .data(yScale.ticks(6))
      .enter()
      .append("line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", (d) => yScale(d))
      .attr("y2", (d) => yScale(d))
      .attr("stroke", "#f3f4f6")
      .attr("stroke-width", 1);

    // Period markers
    const date1 = filters?.date1;
    const date2 = filters?.date2;
    if (date1) {
      const x1 = xScale(new Date(date1));
      if (x1 >= 0 && x1 <= width) {
        g.append("line")
          .attr("x1", x1).attr("x2", x1)
          .attr("y1", 0).attr("y2", height)
          .attr("stroke", "#dc2626")
          .attr("stroke-width", 1.5)
          .attr("stroke-dasharray", "5,3");
        g.append("text")
          .attr("x", x1).attr("y", -6)
          .attr("text-anchor", "middle")
          .style("font-size", "10px")
          .style("font-weight", "700")
          .style("fill", "#dc2626")
          .text("P1");
      }
    }
    if (date2) {
      const x2 = xScale(new Date(date2));
      if (x2 >= 0 && x2 <= width) {
        g.append("line")
          .attr("x1", x2).attr("x2", x2)
          .attr("y1", 0).attr("y2", height)
          .attr("stroke", "#16a34a")
          .attr("stroke-width", 1.5)
          .attr("stroke-dasharray", "5,3");
        g.append("text")
          .attr("x", x2).attr("y", -6)
          .attr("text-anchor", "middle")
          .style("font-size", "10px")
          .style("font-weight", "700")
          .style("fill", "#16a34a")
          .text("P2");
      }
    }

    // Draw lines + points per center
    const parseDate = timeParse("%Y-%m-%d");

    centerNames.forEach((center) => {
      const data = rawData.centers[center];
      const color = CENTER_COLORS[center] || "#6b7280";

      const lineGen = line()
        .x((d) => xScale(parseDate(d.date)))
        .y((d) => yScale(d.value));

      g.append("path")
        .datum(data)
        .attr("fill", "none")
        .attr("stroke", color)
        .attr("stroke-width", 2)
        .attr("d", lineGen);

      // Points with hover
      const points = g.selectAll(`.dot-${center}`)
        .data(data)
        .enter()
        .append("circle")
        .attr("class", `ts-dot ts-dot-${center}`)
        .attr("cx", (d) => xScale(parseDate(d.date)))
        .attr("cy", (d) => yScale(d.value))
        .attr("r", 3)
        .attr("fill", color)
        .attr("stroke", "#fff")
        .attr("stroke-width", 1)
        .style("cursor", "pointer")
        .style("opacity", 0.85);

      // Tooltip via native title
      points.append("title").text((d) =>
        `${center} — ${d.date}\n${fmtY(rawData.metric, d.value)}`
      );

      // Hover enlarge
      points
        .on("mouseenter", function () {
          select(this).attr("r", 6).style("opacity", 1);
        })
        .on("mouseleave", function () {
          select(this).attr("r", 3).style("opacity", 0.85);
        });
    });

    // Legend
    const legend = g.append("g")
      .attr("transform", `translate(${width + 10}, 0)`);

    centerNames.forEach((center, i) => {
      const legendRow = legend.append("g")
        .attr("transform", `translate(0, ${i * 18})`);
      legendRow.append("rect")
        .attr("width", 12)
        .attr("height", 12)
        .attr("fill", CENTER_COLORS[center] || "#6b7280")
        .attr("rx", 2);
      legendRow.append("text")
        .attr("x", 16)
        .attr("y", 10)
        .style("font-size", "11px")
        .style("fill", "#374151")
        .text(center);
    });
  }, [rawData, filters, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      title={`Full Timeseries — ${stationName || "Station"}`}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={850}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin tip="Loading timeseries data..." />
        </div>
      ) : rawData && rawData.centers && Object.keys(rawData.centers).length > 0 ? (
        <div className="compare-ts-modal">
          <div className="compare-ts-modal__subtitle">
            {METRIC_LABELS[rawData.metric] || rawData.metric} — last 30 days
          </div>
          <div className="compare-ts-modal__chart">
            <svg ref={svgRef} />
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
          No timeseries data available for this station.
        </div>
      )}
    </Modal>
  );
};

export default CompareTimeseriesModal;
